/**
 * SpotlightOverlay — one "Show me" step.
 *
 * Dims the page, keeps one real element bright with a saffron ring, and shows a
 * small card: "STEP 1 OF N · LESSON", a title, one sentence, Exit / Next.
 * → moves on, Esc exits. The page under the dim cannot be clicked.
 *
 * It draws nothing of its own besides the dim, the ring and the card (the
 * app's own `.card` / `.btn` classes), so it is mounted inside `.aura-root`.
 */
import { useEffect, useLayoutEffect, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';

export interface SpotlightOverlayProps {
  /** The element(s) to keep bright: a CSS selector (one ring around every match) or a ref. Missing / not found → no ring. */
  target?: string | RefObject<HTMLElement | null>;
  lessonTitle: string;
  step: number;
  stepCount: number;
  title: string;
  body: string;
  onNext: () => void;
  onExit: () => void;
  /** Label of the forward button. Defaults to "Next", or "Done" on the last step. */
  nextLabel?: string;
}

interface Box { top: number; left: number; width: number; height: number }

const PAD = 6;
const CARD_WIDTH = 320;
const DIM = 'rgba(27, 23, 19, 0.62)';

function resolve(target: SpotlightOverlayProps['target']): HTMLElement[] {
  if (!target) return [];
  if (typeof target !== 'string') return target.current ? [target.current] : [];
  try { return Array.from(document.querySelectorAll<HTMLElement>(target)); } catch { return []; }
}

function useTargetBox(target: SpotlightOverlayProps['target']): Box | null {
  const [box, setBox] = useState<Box | null>(null);
  useLayoutEffect(() => {
    let frame = 0;
    let scrolled = false;
    const measure = () => {
      const found = resolve(target);
      // Bring the target into view once, also when it mounts a moment after the step starts.
      if (!scrolled && found[0]) { scrolled = true; found[0].scrollIntoView?.({ block: 'nearest', inline: 'nearest' }); }
      const rects = found.map((el) => el.getBoundingClientRect()).filter((rect) => rect.width > 0 || rect.height > 0);
      const top = Math.min(...rects.map((rect) => rect.top));
      const left = Math.min(...rects.map((rect) => rect.left));
      const next = rects.length > 0
        ? { top: top - PAD, left: left - PAD, width: Math.max(...rects.map((rect) => rect.right)) - left + PAD * 2, height: Math.max(...rects.map((rect) => rect.bottom)) - top + PAD * 2 }
        : null;
      setBox((prev) => (prev && next && prev.top === next.top && prev.left === next.left && prev.width === next.width && prev.height === next.height) || (!prev && !next) ? prev : next);
    };
    measure();
    // The target can mount a moment later (lazy page, data load) — keep looking.
    const timer = window.setInterval(measure, 300);
    const onMove = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure); };
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.clearInterval(timer);
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [target]);
  return box;
}

export function SpotlightOverlay({ target, lessonTitle, step, stepCount, title, body, onNext, onExit, nextLabel }: SpotlightOverlayProps) {
  const box = useTargetBox(target);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); onNext(); }
      if (event.key === 'Escape') { event.preventDefault(); onExit(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onNext, onExit]);

  const viewportW = typeof window === 'undefined' ? 1280 : window.innerWidth;
  const viewportH = typeof window === 'undefined' ? 800 : window.innerHeight;
  // Card sits under the target, or above it when there is no room below; centred when there is no target.
  const below = box ? box.top + box.height + 12 : 0;
  const cardStyle: React.CSSProperties = box
    ? {
        left: Math.max(12, Math.min(box.left, viewportW - CARD_WIDTH - 12)),
        ...(below + 190 <= viewportH || box.top < 200 ? { top: Math.min(below, Math.max(12, viewportH - 200)) } : { bottom: viewportH - box.top + 12 }),
      }
    : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };

  const overlay = (
    <div data-tour-spotlight role="dialog" aria-modal="true" aria-label={`${lessonTitle} — step ${step} of ${stepCount}`}>
      {/* Click shield: the page under the walk is look-only. */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 80, background: box ? 'transparent' : DIM }} />
      {box && (
        <div
          data-tour-ring
          style={{
            position: 'fixed', zIndex: 81, pointerEvents: 'none',
            top: box.top, left: box.left, width: box.width, height: box.height,
            borderRadius: 'var(--r-md)',
            border: '2px solid var(--saffron)',
            boxShadow: `0 0 0 4px var(--saffron-100), 0 0 0 9999px ${DIM}`,
          }}
        />
      )}
      <div className="card p-4" style={{ position: 'fixed', zIndex: 82, width: CARD_WIDTH, maxWidth: 'calc(100vw - 24px)', boxShadow: 'var(--sh-3)', ...cardStyle }}>
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--saffron-600)' }}>
          Step {step} of {stepCount} · {lessonTitle}
        </p>
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="mt-1 text-sm" style={{ color: 'var(--ink-2)' }}>{body}</p>
        <div className="mt-3 flex items-center justify-between gap-2">
          <button type="button" className="btn btn-sm btn-ghost" onClick={onExit}>Exit</button>
          <button type="button" className="btn btn-sm btn-primary" onClick={onNext}>
            {nextLabel ?? (step >= stepCount ? 'Done' : 'Next')} <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </div>
  );
  const host = typeof document === 'undefined' ? null : document.querySelector('.aura-root') ?? document.body;
  return host ? createPortal(overlay, host) : null;
}
